import robotIconUrl from "./iconfont/robot.png";

export type LivechatEnv = "uat" | "test" | "prod";

export type LivechatChannelConfig = {
  channel_id: string;
  api_secret: string;
  api_key: string;
};

export type LivechatTokenResponse =
  | { access_token: string }
  | { data?: { access_token?: string } }
  | Record<string, unknown>;

export type LivechatWidgetOptions = {
  /**
   * Mount target. Defaults to document.body.
   * - HTMLElement: mount into that element
   * - string: treated as CSS selector
   */
  mountTo?: HTMLElement | string;

  /** Controls token endpoint host & default channel values. */
  env?: LivechatEnv;

  /**
   * Token API channel config. If omitted, uses built-in demo channel for prod/test.
   * WARNING: putting api_secret in frontend is not secure for real products.
   */
  channel?: Partial<LivechatChannelConfig>;

  /**
   * Iframe base URL (no query). Defaults to demo client.
   * Example: "https://livechat-client-demo.airudder.com/midea/"
   */
  iframeBaseUrl?: string;

  /** Iframe language param. Default: "en" */
  lang?: string;

  /** Floating position in px. Default: right=20, bottom=20 */
  position?: { right?: number; bottom?: number };

  /** Root z-index. Default: 99999 */
  zIndex?: number;

  /** LocalStorage key used to persist user id. Default: "user_id" */
  userIdStorageKey?: string;

  /**
   * Optional override for user id generation (must be stable across sessions).
   * If not provided, SDK will store one in localStorage.
   */
  getUserId?: () => string;

  /**
   * Optional override to get token (e.g. call your backend).
   * If provided, SDK will NOT call Airudder token API directly.
   */
  getToken?: (ctx: { userId: string }) => Promise<string>;

  /** Error hook */
  onError?: (err: unknown) => void;
};

export type LivechatWidgetInstance = {
  mount: (mountTo?: HTMLElement | string) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  destroy: () => void;
  getAccessToken: () => string | null;
  setLang: (lang: string) => void;
};

const FALLBACK_IFRAME_BASE_URL = "//livechat-client-demo.airudder.com/midea/";
const FALLBACK_TOKEN_HOST = "livechat.airudder.com";

const BUILD_ENV: LivechatEnv =
  (import.meta.env?.VITE_LIVECHAT_ENV as LivechatEnv | undefined) ?? "prod";

const BUILD_IFRAME_BASE_URL: string =
  import.meta.env?.VITE_LIVECHAT_IFRAME_BASE_URL ?? FALLBACK_IFRAME_BASE_URL;

const BUILD_TOKEN_HOST: string =
  import.meta.env?.VITE_LIVECHAT_TOKEN_HOST ?? FALLBACK_TOKEN_HOST;

const BUILD_CHANNEL: LivechatChannelConfig | null = (() => {
  const channel_id = import.meta.env?.VITE_LIVECHAT_CHANNEL_ID;
  const api_key = import.meta.env?.VITE_LIVECHAT_API_KEY;
  const api_secret = import.meta.env?.VITE_LIVECHAT_API_SECRET;
  if (!channel_id || !api_key || !api_secret) return null;
  return { channel_id, api_key, api_secret };
})();

const DEFAULTS = {
  iframeBaseUrl: BUILD_IFRAME_BASE_URL,
  lang: "ja",
  position: { right: 20, bottom: 20 },
  zIndex: 99999,
  userIdStorageKey: "user_id",
  env: BUILD_ENV,
};

function resolveMountTarget(mountTo?: HTMLElement | string): HTMLElement {
  if (!mountTo) return document.body;
  if (mountTo instanceof HTMLElement) return mountTo;
  const el = document.querySelector(mountTo);
  if (!el || !(el instanceof HTMLElement)) return document.body;
  return el;
}

function ensureStyle(): void {
  const id = "livechat-demo-sdk-style";
  if (document.getElementById(id)) return;

  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
.lcsd-root{position:fixed;display:flex;flex-direction:column;align-items:flex-end;gap:12px;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,"Apple Color Emoji","Segoe UI Emoji";line-height:1.2;pointer-events:none}
.lcsd-root *{box-sizing:border-box}
.lcsd-btn{position:relative;z-index:1;isolation:isolate;pointer-events:auto;width:56px;height:56px;padding:0;border-radius:999px;border:none;outline:none;background:#fff;background-color:#fff;color:#111;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 10px 30px rgba(0,0,0,.14);user-select:none;-webkit-appearance:none;appearance:none;-webkit-tap-highlight-color:transparent;transition:transform .12s ease}
.lcsd-btn:hover,.lcsd-btn:focus,.lcsd-btn:active,.lcsd-btn:focus-within{background:#fff;background-color:#fff;color:#111;outline:none}
.lcsd-btn:hover{transform:scale(1.02)}
.lcsd-btn:active{transform:scale(.98)}
.lcsd-btn:focus-visible{outline:2px solid rgba(0,0,0,.2);outline-offset:2px}
.lcsd-btn-icon{display:block;width:36px;height:36px;object-fit:contain;pointer-events:none}
.lcsd-tip{pointer-events:auto;position:relative;max-width:220px;margin-right:2px;padding:10px 14px;border-radius:14px;background:#fff;color:#111;font-size:13px;font-weight:600;box-shadow:0 10px 30px rgba(0,0,0,.14);cursor:pointer;white-space:normal;word-break:break-word;text-align:center;transition:transform .12s ease}
.lcsd-tip:hover{transform:translateY(-1px)}
.lcsd-tip::after{content:"";position:absolute;right:22px;bottom:-6px;width:12px;height:12px;background:#fff;transform:rotate(45deg);box-shadow:2px 2px 4px rgba(0,0,0,.06)}
.lcsd-root[data-open="true"] .lcsd-tip{display:none}
.lcsd-panel{pointer-events:auto;width:320px;height:550px;border-radius:14px;overflow:hidden;background:#fff;box-shadow:0 18px 50px rgba(0,0,0,.22);transform-origin:right bottom;opacity:0;transform:translateY(18px) scale(.65);transition:opacity .24s ease,transform .32s cubic-bezier(.34,1.56,.64,1);display:none;position:relative}
.lcsd-root[data-open="true"] .lcsd-panel{display:block;opacity:1;transform:translateY(0) scale(1)}
.lcsd-iframe{width:100%;height:100%;border:0;display:block}
.lcsd-loading{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.75);backdrop-filter:blur(3px)}
.lcsd-spinner{width:26px;height:26px;border-radius:999px;border:3px solid rgba(0,0,0,.14);border-top-color:rgba(0,0,0,.6);animation:lcsdspin .9s linear infinite}
@keyframes lcsdspin{to{transform:rotate(360deg)}}
.lcsd-error{position:absolute;inset:0;display:flex;flex-direction:column;gap:10px;align-items:center;justify-content:center;background:rgba(255,255,255,.92);padding:18px;text-align:center}
.lcsd-error-title{font-size:14px;color:#111;font-weight:650}
.lcsd-error-msg{font-size:12px;color:rgba(0,0,0,.65);max-width:260px;word-break:break-word}
.lcsd-retry{border:1px solid rgba(0,0,0,.2);background:#fff;border-radius:10px;padding:8px 10px;cursor:pointer;font-size:12px}
.lcsd-retry:hover{background:rgba(0,0,0,.03)}
`;
  document.head.appendChild(style);
}

function normalizeUrlBase(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return DEFAULTS.iframeBaseUrl;
  return trimmed.endsWith("/") ? trimmed : `${trimmed}/`;
}

/**
 * iframe 内通过 JSON-RPC 通知容器变更：
 *   { jsonrpc: "2.0", method: "setContainer", params: { styles: { width: 0, height: 0 } } }
 * 当 width 或 height 被设为 0 时，视为关闭面板。
 */
function isIframeCloseMessage(data: unknown): boolean {
  if (!data || typeof data !== "object") return false;
  const o = data as Record<string, unknown>;
  if (o.jsonrpc !== "2.0" || o.method !== "setContainer") return false;
  const params = o.params as { styles?: Record<string, unknown> } | undefined;
  const styles = params?.styles;
  if (!styles || typeof styles !== "object") return false;
  const isZero = (v: unknown) => v === 0 || v === "0" || v === "0px";
  return isZero(styles.width) || isZero(styles.height);
}

function getOrCreateUserId(storageKey: string): string {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed === "string" && parsed.length > 0) return parsed;
    }
  } catch {
    // ignore
  }

  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `uid_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;

  try {
    localStorage.setItem(storageKey, JSON.stringify(id));
  } catch {
    // ignore
  }
  return id;
}

function toHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let out = "";
  for (const b of bytes) {
    const h = b.toString(16);
    out += h.length === 1 ? `0${h}` : h;
  }
  return out;
}

async function hmacSha256Hex(key: string, message: string): Promise<string> {
  if (!crypto?.subtle) {
    throw new Error("WebCrypto 不可用，无法在浏览器端计算 HMAC-SHA256。");
  }
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, enc.encode(message));
  return toHex(sig);
}

async function defaultGetToken(opts: {
  channel: LivechatChannelConfig;
  userId: string;
}): Promise<string> {
  const url = `https://${BUILD_TOKEN_HOST}/api/livechat/token`;
  const body = JSON.stringify({
    user_id: opts.userId,
    channel_id: opts.channel.channel_id,
  });
  const sign = await hmacSha256Hex(opts.channel.api_secret, body);

  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "X-AIRudder-Sign": sign,
      "X-AIRudder-Sign-Key": opts.channel.api_key,
    },
    body,
  });

  if (!resp.ok) {
    throw new Error(`获取 token 失败：HTTP ${resp.status}`);
  }

  const json = (await resp.json()) as LivechatTokenResponse;
  const token =
    (json as any)?.access_token ??
    (json as any)?.data?.access_token ??
    (json as any)?.data?.data?.access_token;
  if (!token || typeof token !== "string") {
    throw new Error("获取 token 失败：响应里未找到 access_token");
  }
  return token;
}

export function createLivechatWidget(
  options: LivechatWidgetOptions = {},
): LivechatWidgetInstance {
  ensureStyle();

  const iframeBaseUrl = normalizeUrlBase(
    options.iframeBaseUrl ?? DEFAULTS.iframeBaseUrl,
  );
  let lang = options.lang ?? DEFAULTS.lang;
  const zIndex = options.zIndex ?? DEFAULTS.zIndex;
  const position = {
    right: options.position?.right ?? DEFAULTS.position.right,
    bottom: options.position?.bottom ?? DEFAULTS.position.bottom,
  };
  const userIdStorageKey =
    options.userIdStorageKey ?? DEFAULTS.userIdStorageKey;

  const channel: LivechatChannelConfig | null = (() => {
    const base = BUILD_CHANNEL ?? ({} as Partial<LivechatChannelConfig>);
    const merged = { ...base, ...(options.channel ?? {}) };
    if (
      typeof merged.channel_id === "string" &&
      typeof merged.api_key === "string" &&
      typeof merged.api_secret === "string"
    ) {
      return merged as LivechatChannelConfig;
    }
    return null;
  })();

  let root: HTMLDivElement | null = null;
  let panel: HTMLDivElement | null = null;
  let iframeEl: HTMLIFrameElement | null = null;
  let loadingEl: HTMLDivElement | null = null;
  let errorEl: HTMLDivElement | null = null;

  let mounted = false;
  let open = false;
  let iframeLoaded = false;
  let accessToken: string | null = null;
  let tokenPromise: Promise<string> | null = null;

  function setOpen(next: boolean) {
    open = next;
    if (root) root.dataset.open = String(open);
  }

  function setLoading(isLoading: boolean) {
    if (!panel) return;
    if (!loadingEl) return;
    loadingEl.style.display = isLoading ? "flex" : "none";
  }

  function setError(err: unknown) {
    if (!panel || !errorEl) return;
    const msgEl = errorEl.querySelector(".lcsd-error-msg");
    if (msgEl) {
      const text =
        err instanceof Error ? err.message : typeof err === "string" ? err : "";
      msgEl.textContent = text || "未知错误";
    }
    errorEl.style.display = "flex";
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.style.display = "none";
  }

  function buildIframeSrc(token: string) {
    const u = new URL(iframeBaseUrl, window.location.href);
    u.searchParams.set("token", token);
    u.searchParams.set("lang", lang);
    return u.toString();
  }

  async function ensureToken(): Promise<string> {
    if (accessToken) return accessToken;
    if (tokenPromise) return tokenPromise;

    const userId = options.getUserId
      ? options.getUserId()
      : getOrCreateUserId(userIdStorageKey);

    tokenPromise = (async () => {
      try {
        const t = options.getToken
          ? await options.getToken({ userId })
          : await (async () => {
              if (!channel) {
                throw new Error(
                  "缺少 channel 配置（channel_id/api_key/api_secret），无法直接调用 token API。",
                );
              }
              return await defaultGetToken({ channel, userId });
            })();
        accessToken = t;
        return t;
      } finally {
        tokenPromise = null;
      }
    })();

    return tokenPromise;
  }

  function ensureDom() {
    if (root) return;

    root = document.createElement("div");
    root.className = "lcsd-root";
    root.dataset.open = "false";
    root.style.right = `${position.right}px`;
    root.style.bottom = `${position.bottom}px`;
    root.style.zIndex = String(zIndex);

    panel = document.createElement("div");
    panel.className = "lcsd-panel";

    loadingEl = document.createElement("div");
    loadingEl.className = "lcsd-loading";
    loadingEl.style.display = "none";
    loadingEl.innerHTML = `<div class="lcsd-spinner" aria-label="Loading"></div>`;

    errorEl = document.createElement("div");
    errorEl.className = "lcsd-error";
    errorEl.style.display = "none";
    errorEl.innerHTML = `
      <div class="lcsd-error-title">Chat 加载失败</div>
      <div class="lcsd-error-msg"></div>
      <button class="lcsd-retry" type="button">重试</button>
    `;
    const retryBtn = errorEl.querySelector("button.lcsd-retry");
    retryBtn?.addEventListener("click", () => {
      clearError();
      void loadIframe();
    });

    panel.appendChild(loadingEl);
    panel.appendChild(errorEl);

    const tip = document.createElement("div");
    tip.className = "lcsd-tip";
    tip.setAttribute("role", "button");
    tip.setAttribute("tabindex", "0");
    tip.setAttribute("aria-label", "チャットでのお問い合わはこちら");
    tip.innerHTML = "チャットでのお問い合わ<br>はこちら";
    tip.addEventListener("click", () => api.open());
    tip.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        api.open();
      }
    });

    const btn = document.createElement("button");
    btn.className = "lcsd-btn";
    btn.type = "button";
    btn.setAttribute("aria-label", "Open chat");
    btn.innerHTML = `<img class="lcsd-btn-icon" src="${robotIconUrl}" width="36" height="36" alt="" aria-hidden="true" />`;
    btn.addEventListener("click", () => api.toggle());

    root.appendChild(panel);
    root.appendChild(tip);
    root.appendChild(btn);
  }

  function sendStartConversation() {
    iframeEl?.contentWindow?.postMessage(
      {
        jsonrpc: "2.0",
        id: "#startConversation",
        method: "startConversation",
        params: {},
      },
      "*",
    );
  }

  async function loadIframe() {
    if (!panel) return;
    clearError();

    try {
      setLoading(true);
      const token = await ensureToken();
      const src = buildIframeSrc(token);

      if (!iframeEl) {
        iframeEl = document.createElement("iframe");
        iframeEl.className = "lcsd-iframe";
        iframeEl.setAttribute(
          "allow",
          "geolocation; midi; encrypted-media; microphone; camera; fullscreen; autoplay; display-capture",
        );
        iframeEl.allowFullscreen = true;
        iframeEl.setAttribute("title", "Livechat");
        iframeEl.addEventListener("load", () => {
          iframeLoaded = true;
        });
        panel.appendChild(iframeEl);
      }
      if (iframeEl.src !== src) {
        iframeLoaded = false;
        iframeEl.src = src;
      }
    } catch (err) {
      options.onError?.(err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  const api: LivechatWidgetInstance = {
    mount(mountTo) {
      if (mounted) return;
      ensureDom();
      const target = resolveMountTarget(mountTo ?? options.mountTo);
      target.appendChild(root!);
      mounted = true;
    },
    open() {
      if (!mounted) api.mount();
      setOpen(true);
      if (!accessToken || !iframeEl) {
        void loadIframe();
        return;
      }
      if (iframeLoaded) sendStartConversation();
    },
    close() {
      setOpen(false);
    },
    toggle() {
      if (!open) api.open();
      else api.close();
    },
    destroy() {
      window.removeEventListener("message", onIframePostMessage);
      if (root?.parentElement) root.parentElement.removeChild(root);
      root = null;
      panel = null;
      iframeEl = null;
      loadingEl = null;
      errorEl = null;
      mounted = false;
      open = false;
      iframeLoaded = false;
      accessToken = null;
      tokenPromise = null;
    },
    getAccessToken() {
      return accessToken;
    },
    setLang(nextLang: string) {
      lang = nextLang || DEFAULTS.lang;
      if (accessToken && iframeEl) {
        iframeLoaded = false;
        iframeEl.src = buildIframeSrc(accessToken);
      }
    },
  };

  function onIframePostMessage(ev: MessageEvent): void {
    if (!iframeEl || ev.source !== iframeEl.contentWindow) return;
    if (!isIframeCloseMessage(ev.data)) return;
    api.close();
  }
  window.addEventListener("message", onIframePostMessage);

  return api;
}
